import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, User, LogOut, LayoutDashboard, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

const NAV_LINKS = [
  { href: '/', label: 'Inicio' },
  { href: '/experiencias', label: 'Experiencias' },
  { href: '/nosotros', label: 'Nosotros' },
  { href: '/contacto', label: 'Concierge' },
];

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <span className="text-xl md:text-2xl font-bold tracking-tighter text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              ELITE GOLF <span className="text-zinc-500">MX</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm tracking-wide transition-colors duration-300 ${
                  location.pathname === link.href ? 'text-white' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Auth */}
            {user ? (
              <div className="hidden md:flex items-center gap-3">
                {user.is_admin && (
                  <Link to="/admin" className="text-zinc-400 hover:text-white transition-colors" data-testid="admin-link">
                    <LayoutDashboard size={18} />
                  </Link>
                )}
                <Link to="/perfil" className="text-zinc-400 hover:text-white transition-colors" data-testid="profile-link">
                  <Crown size={18} />
                </Link>
                <span className="text-xs text-zinc-500">{user.name?.split(' ')[0]}</span>
                <button onClick={logout} className="text-zinc-400 hover:text-white transition-colors" data-testid="logout-button">
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <button
                onClick={login}
                className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid="login-button"
              >
                <User size={18} />
                <span>Entrar</span>
              </button>
            )}

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-2 text-zinc-400 hover:text-white" data-testid="mobile-menu-trigger">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#050505] border-l border-white/10 w-72 p-0">
                <div className="flex flex-col h-full pt-16 px-6">
                  {NAV_LINKS.map(link => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className="py-4 text-lg text-zinc-300 hover:text-white border-b border-white/5 transition-colors"
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="mt-8">
                    {user ? (
                      <div className="space-y-4">
                        <p className="text-sm text-zinc-500">{user.email}</p>
                        <Link to="/perfil" onClick={() => setOpen(false)} className="flex items-center gap-2 text-zinc-300 hover:text-white">
                          <Crown size={16} /> Mi Perfil
                        </Link>
                        {user.is_admin && (
                          <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 text-zinc-300 hover:text-white">
                            <LayoutDashboard size={16} /> Admin
                          </Link>
                        )}
                        <button onClick={() => { logout(); setOpen(false); }} className="flex items-center gap-2 text-zinc-400 hover:text-white">
                          <LogOut size={16} /> Cerrar Sesion
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => { login(); setOpen(false); }} className="flex items-center gap-2 text-white" data-testid="mobile-login">
                        <User size={18} /> Iniciar Sesion
                      </button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
